/**
 * The one transport this application speaks, and one failure vocabulary.
 *
 * TRANSPORT: THE P1 HTTP API, PROXIED AT `/api`
   `apps/api` owns every read and every write. It issues the signed keyset
   cursor, the bundle download that records a retrieval, the optimistic-lock
   draft save, and every ACL mutation. Its error body is the contract in
   `apps/api/src/errors.ts`: `{request_id, error:{code, message, detail, meta}}`
   under a fixed `message` per code.
 *
 * THERE IS NO SECOND TRANSPORT, AND THAT IS A CORRECTNESS FACT
   This application used to read and write through PostgREST at `/rest/v1`. That
   surface cannot serve this data: `supabase/config.toml` lists
   `schemas = ["public","graphql_public"]`, so the `app` schema was never visible
   through it. Every listing silently returned zero rows, and the screens rendered
   their chrome over an empty table while looking, from the outside, like a filter
   that matched nothing.
 *
   Adding `app` to that list would make the queries "work" and would be worse than
 * the empty tables, because it would expose tenant tables to any holder of the
 * public anon key with no per-request role assertion — the one thing `apps/api`
   exists to provide. The migration was the only honest fix, so `rest()` and the
   database origin the browser used to be handed are gone rather than left
   available.
 *
 * THE PROXY IS STILL THE ONLY WAY IN
   The browser is handed the *configuredness* of the API (`runtimeConfig.apiBase`)
   and nothing else. A request goes to this process's own `/api` prefix, which
   `server.ts` forwards. That is deliberate and is not a shortcut:
 *
   - `apps/api` sets no CORS headers, so a direct call is a request that fails in
     the browser rather than one that is merely refused.
   - The API's address is server configuration. Handing it to the browser would
     publish the internal address of the service to every reader.
 *   - An unconfigured API must render as "this deployment has no API" and must
     never issue a request, which is why the configuredness check stays on this
     side of the send rather than relying on the proxy's 503.
 *
 * ONE FAILURE TYPE, BECAUSE THE SCREENS SHARE THEM
   Every view renders the same states, so every call resolves to the same
 *   discriminated union. Two things matter for correctness:
 *
   - A refusal is not a transport failure. `classifyDatabaseMessage` reads the
     `acl: <reason>: <detail>` protocol the migrations declare rather than
     pattern-matching HTTP status, because rendering a refusal as "something went
     wrong" would hide the one sentence that explains the outcome.
 *
   - A record the policies hide is a 404, not an empty list, and the API produces
 *     it from one filtered primary-key lookup. That is why a hidden record and a
 *     missing one render the same screen: there is no point in the code where they
 *     ever differed. A LISTING the policies hide is a genuinely empty page, which
 *     is why `queries.ts` reads "are there more" off `page.nextCursor` and never
 *     off the row count.
 *
 * `unavailable` is not `not-configured`. The first means this deployment has no
   such route because the lifecycle function it would call is not in the schema;
 * the second means an environment variable is unset and setting it would fix the
   screen. Telling someone to restart with a variable that changes nothing sends
   them looking for a fix that is not there.
 */

export type Failure =
  | { readonly kind: 'not-configured'; readonly what: string }
  | { readonly kind: 'offline' }
  | { readonly kind: 'unauthenticated' }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'not-found'; readonly reason: string }
  | {
      readonly kind: 'conflict';
      readonly reason: string;
      readonly detail: string;
      readonly currentVersion: number | null;
    }
  | { readonly kind: 'invalid'; readonly reason: string; readonly detail: string }
  /**
   * `retryAfterSeconds` is the server's `Retry-After`, read from the header.
   * It is null when the 429 carried no readable deadline, which is a different
   * fact from "retry in zero seconds" and is rendered as a different sentence.
   */
  | { readonly kind: 'throttled'; readonly retryAfterSeconds: number | null }
  | { readonly kind: 'refused'; readonly reason: string; readonly detail: string }
  | { readonly kind: 'server'; readonly detail: string; readonly requestId: string | null }
  /**
   * The operation has no route in this build, and no row write stands in for it.
   *
   * This is NOT `not-configured`: that means an environment variable is unset and
   * setting it would fix the screen. This one means the deployment has no such
   * operation at all, because the lifecycle RPC it would call does not exist in
   * the schema. Telling a person to restart with a different variable would send
   * them looking for a fix that is not there, so the two are kept apart.
   */
  | { readonly kind: 'unavailable'; readonly what: string }
  | { readonly kind: 'malformed'; readonly detail: string };

export type Result<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly failure: Failure };

export function ok<T>(value: T): Result<T> {
  return { ok: true, value };
}

export function fail<T>(failure: Failure): Result<T> {
  return { ok: false, failure };
}

/**
 * What the server told the page about signing in.
 *
 * `reason` is the sentence, not a code: the person reading the login screen is
 * being told which variables are unset or which provider call failed, and a
 * token they would have to decode is not that. It is null exactly when
 * `available` is true, so a screen cannot render a live control and a reason
 * at once.
 */
export interface SignInState {
  readonly available: boolean;
  readonly reason: string | null;
}

interface RuntimeConfig {
  /** Origin of `apps/api`, or null when the web process was started without one. */
  readonly apiBase: string | null;
  /**
   * Whether the sign-in control may be live, and why not when it may not.
   *
   * This replaced `authBase`, a base URL the login screen appended `/otp` and
   * `/magiclink` to. The provider serves neither as a page, so the enabled
   * branch pointed at addresses that answered 404 while claiming to work. The
   * authorization URL is not published here either: the `state` and
   * `code_verifier` behind it are server-side and single-use, so the link is
   * `/auth/signin` and the server issues the real request.
   */
  readonly signIn: SignInState;
}

/*
 * `RuntimeConfig` deliberately carries no database origin, and did use to.
 *
 * The browser read through PostgREST and needed one. It does not any more:
 * `supabase/config.toml` lists `schemas = ["public","graphql_public"]`, so `app`
 * was never visible through PostgREST and every list came back empty. Handing the
 * browser a database origin it cannot usefully reach would also publish the
 * internal address of the data tier to every reader. The web process still needs
 * that origin to proxy `/auth/v1/...`; it just does not tell the page about it.
 * The two `/auth` paths this process answers itself — `/auth/signin` and
 * `/auth/callback` — are answered here rather than proxied, and were
 * unreachable while a database was configured, because `/auth` is a proxied
 * prefix and the callback fell straight through to the identity provider.
 */

declare global {
  interface Window {
    __SKILLS_MANAGER__?: Partial<RuntimeConfig>;
  }
}

// Read through `globalThis` rather than `window` so this module can be loaded
// where there is no window at all (a test runner, a build step) instead of
// throwing at import time. In a browser `globalThis` IS `window`, so the value
// is the same object; the only difference is that its absence is a null
// configuration rather than a crash.
const injected: Partial<RuntimeConfig> =
  typeof globalThis === 'undefined' ? {} : ((globalThis as unknown as Window).__SKILLS_MANAGER__ ?? {});

// Absent means "this page was served without a runtime config", which is not
// the same as "the operator configured nothing" — the first has no reason to
// give and the second names the variables. Both render as unavailable; only
// the second can say why.
const runtimeConfig: RuntimeConfig = {
  apiBase: injected.apiBase ?? null,
  signIn: injected.signIn ?? { available: false, reason: 'This page was served without a runtime config.' },
};

export type TokenSource = () => string | null;

interface RawResponse {
  readonly status: number;
  readonly body: string;
  readonly requestId: string | null;
  /**
   * The server's `Retry-After` in whole seconds, or null when it sent none.
   *
   * Read from the header rather than assumed. `apps/api` publishes an EXACT
   * deadline, not an estimate — `ratelimit.ts:42-44` says so and
   * `ratelimit.ts:210` writes `String(Math.max(1, Math.ceil(retryAfterSeconds)))`
   * onto the 429. This process forwards the header (`server.ts:416` lists
   * `retry-after` among the names it copies), so the number arrives at the
   * browser and is thrown away here. A literal substituted for it is a status
   * number that did not come from the server, which is V1 P3's gate sentence
   * (`docs/V1-IMPLEMENTATION.md:57`) failing on the one badge that can be
   * checked without a browser.
   *
   * Null means the header was absent, and it is kept distinct from zero: an
   * absent deadline is not a deadline of zero seconds.
   */
  readonly retryAfterSeconds: number | null;
  /**
   * The body as bytes.
   *
   * `body` above is a DECODE of these bytes, and a decode is lossy for
   * anything that is not text. A zip archive read through `response.text()` is
   * corrupted the moment it is read, and the corruption is silent — the file
   * still opens, as a different file. That is not hypothetical here: `GET
   * /v1/skills/:skillId/export` serves the standard-folder archive, and a
   * person handed a mangled one would blame the release rather than the client
   * that mangled it.
   *
   * So the bytes are read once, here, and decoded on demand for the callers
   * that want text — not the other way round.
   */
  readonly bytes: Uint8Array<ArrayBuffer>;
}

/**
 * The `Retry-After` header as whole seconds, or null when it is absent or
 * unreadable.
 *
 * A non-integer or negative value is refused rather than coerced. The server
 * only ever writes a positive integer (`ratelimit.ts:210`, asserted by
 * `apps/api/test/ratelimit.test.ts:142-144`), so anything else is a body this
 * client does not understand, and answering "0 seconds" or "-3 seconds" to it
 * would replace one invented number with a worse one.
 */
function parseRetryAfter(value: string | null): number | null {
  if (value === null) return null;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const seconds = Number(trimmed);
  return Number.isSafeInteger(seconds) ? seconds : null;
}

function isJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The `acl: <reason>: <detail>` protocol the migrations declare. */
function classifyDatabaseMessage(message: string): Failure {
  const match = /^acl:\s*([a-z0-9_]+)\s*:\s*([\s\S]*)$/i.exec(message.trim());
  const reason = match?.[1] ?? message.trim();
  const detail = (match?.[2] ?? '').trim();
  if (reason === 'not_found') return { kind: 'not-found', reason: 'not_found' };
  if (reason === 'browser_session_required' || reason === 'forbidden') {
    return { kind: 'forbidden', reason };
  }
  // `invite_expired` collapses to the same `not_found` as `invite_not_found`,
  // and this is the second place that collapse has to hold. On the §6 envelope
  // path `apps/api` has already done it (every 404 code gets the identical
  // `Not found.`, asserted in `apps/api/test/messages.test.ts`), but the branch
  // at the bottom parses a RAW `acl: <reason>: <detail>` body — a
  // PostgREST-shaped response, which this deployment no longer produces and
  // which a future transport could. Left alone it classified as `refused` with
  // `detail: "this invitation expired at 2026-10-01 01:41:08+00"`, and
  // `presentFailure` RENDERS a `refused`'s detail. That is the expiry instant
  // reaching the page, which is the oracle ACL-01 exists to withhold.
  //
  // So the expired invitation and the consumed one are the same failure here,
  // by the same reasoning the database uses when it answers both with
  // `invite_not_found`.
  if (reason === 'invite_expired' || reason === 'invite_not_found') {
    return { kind: 'not-found', reason: 'not_found' };
  }
  if (reason.endsWith('_conflict')) {
    return { kind: 'conflict', reason, detail, currentVersion: null };
  }
  if (reason === 'invalid_package' || reason === 'invalid_request') {
    return { kind: 'invalid', reason, detail };
  }
  if (reason === 'unauthenticated') return { kind: 'unauthenticated' };
  return { kind: 'refused', reason, detail };
}

/**
 * Map a response onto the failure vocabulary.
 *
 * Both transports agree on 401 and 429. The API service publishes a machine
 * `code`; PostgREST publishes the raw SQLSTATE and the message that carries the
 * refusal protocol. Neither is inspected before the status, so an unmapped
 * reason still lands on `refused` rather than on a generic server error.
 */
function readFailure(response: RawResponse): Failure {
  let parsed: unknown = null;
  try {
    parsed = response.body === '' ? null : JSON.parse(response.body);
  } catch {
    parsed = null;
  }

  if (response.status === 401) return { kind: 'unauthenticated' };
  if (response.status === 429) {
    // The server's own number, or null — never a literal. `presentFailure`
    // renders this value verbatim, so a constant here is a rate-limit
    // countdown that disagrees with the deadline the limiter actually set, and
    // a person who trusts it retries early and is throttled again.
    return { kind: 'throttled', retryAfterSeconds: response.retryAfterSeconds };
  }

  if (isJsonObject(parsed) && isJsonObject(parsed.error)) {
    const error = parsed.error;
    const code = typeof error.code === 'string' ? error.code : 'internal_error';
    const detail = typeof error.detail === 'string' ? error.detail : '';
    if (response.status === 403) return { kind: 'forbidden', reason: code };
    // A 404 carries `reason` and drops `detail`, and the asymmetry is the
    // point. `apps/api` already made the collapse one layer up: every 404
    // resource code gets the byte-identical message `Not found.`, and
    // `errors.ts` asserts it (`apps/api/test/messages.test.ts`). This branch
    // does not enforce that — an API consumer that logs `detail` still receives
    // it — it declines to re-open what the service already collapsed.
    //
    // `detail` is the server's own explanation, and for at least one code it is
    // a fact about a row rather than a category: `invite_expired` arrives with
    // `this invitation expired at 2026-10-01 01:41:08+00` in it. Passing that
    // through would name when a specific invitation lapsed, to anyone holding
    // the token. The `code` stays because it names the CATEGORY, which is what
    // §6.1 publishes and what `docs/SUPPORTED-CLIENT-MATRIX.md` §2.3 records
    // the client as entitled to route on; it does not distinguish "does not
    // exist" from "exists and you may not see it", which is the distinction a
    // 404 exists to withhold.
    //
    // So adding a field to this branch is a disclosure decision, not a
    // completeness one, and nothing else in this module decides it for you.
    // `presentFailure` is the other half and is why a `reason` that reaches
    // here still never reaches the page.
    if (response.status === 404) return { kind: 'not-found', reason: code };
    if (response.status === 409) {
      const meta = isJsonObject(error.meta) ? error.meta : {};
      const current = meta.currentVersion;
      return {
        kind: 'conflict',
        reason: code,
        detail: detail === '' ? 'This record has changed since you loaded it.' : detail,
        currentVersion: typeof current === 'number' ? current : null,
      };
    }
    if (response.status === 422) return { kind: 'invalid', reason: code, detail };
    return { kind: 'server', detail, requestId: response.requestId };
  }

  if (isJsonObject(parsed) && typeof parsed.message === 'string') {
    const classified = classifyDatabaseMessage(parsed.message);
    if (response.status >= 500) {
      return {
        kind: 'server',
        detail: classified.kind === 'refused' ? classified.detail : parsed.message,
        requestId: response.requestId,
      };
    }
    return classified;
  }

  if (response.status >= 500) {
    return { kind: 'server', detail: response.body, requestId: response.requestId };
  }
  return { kind: 'malformed', detail: response.body.slice(0, 200) };
}

interface SendOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly headers?: Readonly<Record<string, string>>;
  /**
   * What the caller intends to do with the body.
   *
   * `bytes` exists because the export route serves a zip archive and the other
   * two do not fit it: `json` would fail to parse it and `text` would decode it
   * into a different, still-openable file. Stated as an intent rather than
   * inferred from a `content-type` header because the inference is a second
   * place that decides what a body is.
   */
  readonly expect?: 'json' | 'text' | 'bytes';
}

export interface Client {
  readonly config: RuntimeConfig;
  /** `apps/api`, which wraps its payload in `{request_id, data}`. */
  api<T>(path: string, options?: SendOptions): Promise<Result<T>>;
}

/**
 * One request, one place that decides what "the network failed" means.
 *
 * `fetch` rejects for a refused connection and for an abort, and both of those
 * are the same thing to a person using this application: the request never
 * reached a server. They become `offline`, which the shell renders as a retry
 * rather than as a failure with a request id, because there is no request id.
 */
async function send(
  url: string,
  token: string | null,
  options: SendOptions,
): Promise<Result<RawResponse>> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return fail({ kind: 'offline' });
  }
  // `application/json` is wrong for the one read that is not JSON. Asking for
  // it on the export route would have a content-negotiating server answer with
  // an error envelope describing a request it could not represent, and the
  // person would see a refusal for a read that was about to succeed.
  const headers: Record<string, string> = {
    accept:
      options.expect === 'bytes' ? 'application/zip, application/octet-stream' : 'application/json',
  };
  if (token !== null) {
    // `apikey` is gone with the Data API. It was a PostgREST convention — the
    // anon key is public by design and carries no authority — and `apps/api`
    // authenticates on the bearer token alone.
    headers.authorization = `Bearer ${token}`;
  }
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  for (const [name, value] of Object.entries(options.headers ?? {})) headers[name] = value;

  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      credentials: 'omit',
    });
  } catch {
    return fail({ kind: 'offline' });
  }

  // Read as bytes, always. The string below is decoded from exactly these, so
  // a caller that wants the bytes has them and a caller that wants text gets
  // the same bytes decoded — rather than the two being separate reads that can
  // disagree about what the server sent.
  const bytes = new Uint8Array(await response.arrayBuffer());
  const raw: RawResponse = {
    status: response.status,
    bytes,
    body: new TextDecoder().decode(bytes),
    requestId: response.headers.get('x-request-id'),
    retryAfterSeconds: parseRetryAfter(response.headers.get('retry-after')),
  };
  if (response.status >= 400) return fail(readFailure(raw));
  return ok(raw);
}

function parseJson<T>(raw: RawResponse): Result<T> {
  try {
    return ok(JSON.parse(raw.body) as T);
  } catch {
    return fail({ kind: 'malformed', detail: 'The response was not JSON.' });
  }
}

export function createClient(getToken: TokenSource): Client {
  const call = async <T>(url: string, options: SendOptions): Promise<Result<T>> => {
    const sent = await send(url, getToken(), options);
    if (!sent.ok) return sent;
    if (options.expect === 'bytes') return ok(sent.value.bytes as T);
    if (options.expect === 'text') return ok(sent.value.body as T);
    return parseJson<T>(sent.value);
  };

  return {
    config: runtimeConfig,
    api<T>(path: string, options: SendOptions = {}): Promise<Result<T>> {
      if (runtimeConfig.apiBase === null) {
        return Promise.resolve(fail<T>({ kind: 'not-configured', what: 'the API service' }));
      }
      return call<T>(`/api${path}`, options);
    },
  };
}

/**
 * The envelope `apps/api` wraps every successful payload in. The page cursor is
 * omitted when a listing is not paginated.
 */
export interface ApiEnvelope<T> {
  readonly request_id: string;
  readonly data: T;
  readonly page?: { readonly nextCursor: string | null };
}
